import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api/client.js';
import { useToast } from '../context/ToastContext.jsx';
import PageHeader from '../components/PageHeader.jsx';
import LoadingState from '../components/LoadingState.jsx';
import EmptyState from '../components/EmptyState.jsx';
import StatusBadge from '../components/StatusBadge.jsx';
import Modal from '../components/Modal.jsx';
import ApiKeyRevealModal from '../components/ApiKeyRevealModal.jsx';

export default function InboundSeatsPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const [seats, setSeats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [reveal, setReveal] = useState(null); // { seat, apiKey }

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const list = await api.listInboundSeats();
      setSeats(list);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load inbound seats.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="page">
      <PageHeader
        title="Inbound Seats"
        subtitle="External supply partners (app publishers, ad servers) authorized to call the inbound OpenRTB endpoint."
        actions={
          <button className="btn btn--primary" onClick={() => setShowCreate(true)}>
            + New seat
          </button>
        }
      />

      {loading && <LoadingState label="Loading inbound seats" />}
      {!loading && error && <EmptyState title="Couldn't load inbound seats" message={error} />}

      {!loading && !error && seats && seats.length === 0 && (
        <EmptyState
          title="No inbound seats yet"
          message="Create a seat to let external supply call BidDigi's inbound OpenRTB endpoint."
          action={
            <button className="btn btn--primary" onClick={() => setShowCreate(true)}>
              + New seat
            </button>
          }
        />
      )}

      {!loading && !error && seats && seats.length > 0 && (
        <section className="card card--flush">
          <div className="table-scroll">
            <table className="data-table data-table--clickable">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Seat ID</th>
                  <th>Status</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {seats.map((s) => (
                  <tr key={s.id} onClick={() => navigate(`/inbound-seats/${s.seatId}`)}>
                    <td className="cell-title">{s.name}</td>
                    <td><code className="code-chip">{s.seatId}</code></td>
                    <td>
                      <StatusBadge status={s.status} />
                    </td>
                    <td className="cell-muted">{formatDate(s.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {showCreate && (
        <CreateSeatModal
          onClose={() => setShowCreate(false)}
          onCreated={(result) => {
            setShowCreate(false);
            setReveal(result);
            load();
          }}
        />
      )}

      {reveal && (
        <ApiKeyRevealModal
          title={`API key for "${reveal.seat.name}"`}
          apiKey={reveal.apiKey}
          onClose={() => setReveal(null)}
        />
      )}
    </div>
  );
}

function CreateSeatModal({ onClose, onCreated }) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setFormError(null);
    if (!name.trim()) {
      setFormError('Seat name is required.');
      return;
    }
    setSaving(true);
    try {
      const result = await api.createInboundSeat({ name: name.trim() });
      onCreated(result);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Could not create seat.';
      setFormError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title="New inbound seat" onClose={onClose}>
      <form onSubmit={handleSubmit} className="form">
        <label className="field">
          <span className="field__label">Seat name</span>
          <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Ad Server" autoFocus />
        </label>
        {formError && <div className="form-error">{formError}</div>}
        <div className="form__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn--primary" disabled={saving}>
            {saving ? 'Creating…' : 'Create seat'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
